import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ElementType,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes
} from 'react';
import { primaryButtonGreen } from '../../tokenStyle/tokeReset';

export const classNames = (
  ...values: Array<string | false | null | undefined>
) => values.filter(Boolean).join(' ');

const spacingClassBySize = {
  xs: 'gap-2',
  s: 'gap-3',
  m: 'gap-4',
  l: 'gap-5',
  xl: 'gap-6'
} as const;

export type SpaceSize = keyof typeof spacingClassBySize;

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children?: ReactNode;
  loading?: boolean;
  variant?: 'normal' | 'primary';
}

export const Spinner = ({
  size = 'normal',
  className
}: {
  size?: 'normal' | 'large';
  className?: string;
}) => (
  <span
    aria-hidden="true"
    className={classNames(
      'inline-block animate-spin rounded-full border-2 border-current border-r-transparent',
      size === 'large' ? 'h-8 w-8' : 'h-4 w-4',
      className
    )}
  />
);

export const Button = ({
  children,
  className,
  disabled,
  loading = false,
  style,
  type = 'button',
  variant = 'normal',
  ...props
}: ButtonProps) => (
  <button
    type={type}
    disabled={disabled || loading}
    className={classNames(
      'inline-flex h-9 items-center justify-center rounded-lg border px-3 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2',
      variant === 'primary'
        ? 'text-white hover:brightness-95'
        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50',
      disabled || loading
        ? 'cursor-not-allowed opacity-50 hover:border-inherit hover:bg-inherit'
        : '',
      className
    )}
    style={
      variant === 'primary'
        ? {
            backgroundColor: primaryButtonGreen,
            borderColor: primaryButtonGreen,
            ...style
          }
        : style
    }
    {...props}
  >
    <span className="inline-flex items-center gap-2">
      {loading ? <Spinner className="text-current" /> : null}
      {children}
    </span>
  </button>
);

export const Alert = ({
  children,
  className,
  type = 'info'
}: {
  children?: ReactNode;
  className?: string;
  type?: 'error' | 'warning' | 'success' | 'info';
}) => (
  <div
    role="alert"
    className={classNames(
      'rounded-xl border px-3 py-2 text-sm',
      type === 'error'
        ? 'border-rose-200 bg-rose-50 text-rose-700'
        : type === 'warning'
          ? 'border-amber-200 bg-amber-50 text-amber-800'
          : type === 'success'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
            : 'border-sky-200 bg-sky-50 text-sky-800',
      className
    )}
  >
    {children}
  </div>
);

export const Box = ({
  children,
  className,
  color,
  margin,
  padding,
  textAlign
}: {
  children?: ReactNode;
  className?: string;
  color?: 'text-body-secondary';
  margin?: {
    vertical?: 'xs';
  };
  padding?:
    | 'm'
    | {
        vertical?: 'xl';
      };
  textAlign?: 'center' | 'left' | 'right';
}) => (
  <div
    className={classNames(
      padding === 'm'
        ? 'p-3'
        : typeof padding === 'object' && padding?.vertical === 'xl'
          ? 'py-10'
          : '',
      margin?.vertical === 'xs' ? 'my-2' : '',
      textAlign === 'center'
        ? 'text-center'
        : textAlign === 'right'
          ? 'text-right'
          : textAlign === 'left'
            ? 'text-left'
            : '',
      color === 'text-body-secondary' ? 'text-slate-500' : '',
      className
    )}
  >
    {children}
  </div>
);

interface SpaceBetweenProps {
  children?: ReactNode;
  className?: string;
  direction?: 'horizontal' | 'vertical';
  size?: SpaceSize;
  style?: CSSProperties;
}

export const SpaceBetween = forwardRef<HTMLDivElement, SpaceBetweenProps>(
  ({ children, className, direction = 'vertical', size = 'm', style }, ref) => (
    <div
      className={classNames(
        'flex',
        direction === 'horizontal'
          ? 'flex-row flex-wrap items-center'
          : 'flex-col',
        spacingClassBySize[size],
        className
      )}
      ref={ref}
      style={style}
    >
      {children}
    </div>
  )
);

SpaceBetween.displayName = 'SpaceBetween';

export const Header = ({
  actions,
  children,
  className,
  counter,
  description,
  variant = 'h2'
}: {
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  counter?: ReactNode;
  description?: ReactNode;
  variant?: 'h1' | 'h2' | 'h3';
}) => {
  const TitleTag = variant as ElementType;
  const titleClassName =
    variant === 'h1'
      ? 'text-xl font-semibold tracking-tight text-slate-900'
      : variant === 'h2'
        ? 'text-lg font-semibold tracking-tight text-slate-900'
        : 'text-base font-semibold tracking-tight text-slate-900';

  return (
    <div
      className={classNames(
        'flex w-full min-w-0 flex-wrap items-start justify-between gap-4',
        className
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <TitleTag className={titleClassName}>{children}</TitleTag>
          {counter ? (
            <span className="text-xs font-medium text-slate-400">
              {counter}
            </span>
          ) : null}
        </div>
        {description ? (
          <div
            className="mt-1 truncate whitespace-nowrap text-sm text-slate-500"
            title={typeof description === 'string' ? description : undefined}
          >
            {description}
          </div>
        ) : null}
      </div>
      {actions ? <div className="max-w-full shrink-0">{actions}</div> : null}
    </div>
  );
};

export const Container = ({
  children,
  className,
  fitHeight = false,
  header,
  style
}: {
  children?: ReactNode;
  className?: string;
  fitHeight?: boolean;
  header?: ReactNode;
  style?: CSSProperties;
}) => (
  <section
    className={classNames(
      'w-full min-w-0 rounded-xl border border-slate-200 bg-white shadow-sm',
      fitHeight ? 'flex h-full min-h-0 flex-col overflow-hidden' : '',
      className
    )}
    style={style}
  >
    {header ? (
      <div className="border-b border-slate-200 px-4 py-3">{header}</div>
    ) : null}
    <div
      className={classNames(
        'px-4 py-4',
        fitHeight ? 'min-h-0 flex-1 overflow-hidden' : ''
      )}
    >
      {children}
    </div>
  </section>
);

export const FormField = ({
  children,
  className,
  label
}: {
  children?: ReactNode;
  className?: string;
  label?: ReactNode;
}) => (
  <div className={classNames('space-y-1.5', className)}>
    {label ? (
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
        {label}
      </div>
    ) : null}
    {children}
  </div>
);

export const Input = ({
  className,
  onChange,
  value,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> & {
  onChange?: (event: { detail: { value: string } }) => void;
  value: string;
}) => (
  <input
    {...props}
    value={value}
    onChange={(event) =>
      onChange?.({
        detail: {
          value: event.target.value
        }
      })
    }
    className={classNames(
      'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200',
      className
    )}
  />
);

export interface SelectOptionDefinition {
  label: string;
  value?: string;
  disabled?: boolean;
  description?: string;
}

export const Select = ({
  className,
  disabled,
  onChange,
  options,
  placeholder,
  selectedOption
}: Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> & {
  onChange?: (event: {
    detail: {
      selectedOption: SelectOptionDefinition | null;
    };
  }) => void;
  options: SelectOptionDefinition[];
  placeholder?: string;
  selectedOption: SelectOptionDefinition | null;
}) => {
  const currentValue = selectedOption?.value ?? '';

  return (
    <select
      disabled={disabled}
      value={currentValue}
      onChange={(event) => {
        const nextValue = event.target.value;
        const nextOption =
          options.find(
            (option) => (option.value ?? option.label) === nextValue
          ) ?? null;

        onChange?.({
          detail: {
            selectedOption: nextOption
          }
        });
      }}
      className={classNames(
        'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400',
        className
      )}
    >
      <option value="">{placeholder ?? 'Select an option'}</option>
      {options.map((option) => (
        <option
          key={option.value ?? option.label}
          disabled={option.disabled}
          value={option.value ?? option.label}
        >
          {option.label}
        </option>
      ))}
    </select>
  );
};

export const Popover = ({
  children,
  className,
  content,
  offsetX,
  position = 'top',
  size = 'small'
}: {
  children?: ReactNode;
  className?: string;
  content?: ReactNode;
  dismissButton?: boolean;
  offsetX?: number;
  position?: 'top' | 'bottom';
  size?: 'small' | 'medium';
  triggerType?: 'custom';
}) => (
  <div className={classNames('group relative inline-flex', className)}>
    {children}
    {content ? (
      <div
        role="tooltip"
        style={
          offsetX !== undefined
            ? {
                transform: `translateX(calc(-50% + ${offsetX}px))`
              }
            : undefined
        }
        className={classNames(
          'pointer-events-none invisible absolute left-1/2 z-30 -translate-x-1/2 opacity-0 transition duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100',
          position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
        )}
      >
        <div
          className={classNames(
            'rounded-lg border border-slate-200 bg-slate-900 px-3 py-2 text-center text-white shadow-lg',
            size === 'small' ? 'max-w-xs text-xs' : 'max-w-sm text-sm'
          )}
        >
          {content}
        </div>
      </div>
    ) : null}
  </div>
);
